"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Carousel, CarouselContent, CarouselItem } from "@/components/ui/carousel";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function formatDate(dateStr?: string) {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  } catch {
    return "";
  }
}
function stripHtml(html?: string) {
  if (!html) return "";
  return html.replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim();
}
function estimateReadTime(content?: string, excerpt?: string) {
  const text = stripHtml(content || excerpt || "");
  const words = text.split(/\s+/).filter(Boolean).length;
  const minutes = Math.max(1, Math.round(words / 200));
  return `${minutes} min read`;
}
function ArticleCard({
  post,
  index
}: {
  post: any;
  index: number;
}) {
  const category = post.categories && post.categories.length > 0 ? post.categories[0].name : "Tips";
  const excerpt = stripHtml(post.excerpt);
  const readTime = estimateReadTime(post.content, post.excerpt);
  const date = formatDate(post.date);
  return <article data-index={index} className="group bg-card text-card-foreground transition-all duration-400 ease-[cubic-bezier(0.22,1,0.36,1)] hover:-translate-y-2 hover:shadow-lg overflow-hidden flex flex-col rounded-3xl border border-border">
      <Link to={post.link || "#"} className="block overflow-hidden">
        <div className="aspect-[4/3] w-full overflow-hidden bg-muted" style={{
        borderRadius: "28px 32px 28px 36px / 32px 28px 36px 28px"
      }}>
          {post.featuredImage ? <img src={post.featuredImage} alt={post.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-[400ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.04]" /> : <div className="h-full w-full bg-muted" />}
        </div>
      </Link>

      <div className="p-7 flex flex-col gap-4 flex-1">
        <span className="inline-flex w-fit items-center rounded-lg bg-accent px-3 py-1 text-xs font-bold text-accent-foreground tracking-widest uppercase">
          {category}
        </span>

        <Link to={post.link || "#"} className="block">
          <h3 className="font-default text-2xl font-semibold leading-tight text-foreground line-clamp-3" style={{
          letterSpacing: "-0.01em"
        }}>
            {post.title}
          </h3>
        </Link>

        {excerpt && <p className="text-base text-foreground/80 leading-relaxed line-clamp-2">
            {excerpt}
          </p>}

        <div className="mt-auto flex items-center gap-2 text-xs font-medium text-muted-foreground tracking-wide">
          <span>{readTime}</span>
          <span className="inline-block h-1 w-1 rounded-lg" style={{
          backgroundColor: "var(--primary)"
        }} aria-hidden="true" />
          <span>{date}</span>
        </div>
      </div>
    </article>;
}
function PlaytimeConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <>
        <div className="hidden md:grid grid-cols-3 gap-7">
          {[...Array(3)].map((_, i) => <div key={i} data-index={i} className="bg-card overflow-hidden rounded-3xl border border-border">
              <div className="aspect-[4/3] w-full bg-muted animate-pulse" />
              <div className="p-7 space-y-4">
                <div className="h-6 w-20 bg-muted animate-pulse rounded-lg" />
                <div className="h-6 w-full bg-muted animate-pulse rounded-md" />
                <div className="h-4 w-3/4 bg-muted animate-pulse rounded-md" />
              </div>
            </div>)}
        </div>
        <div className="md:hidden">
          <div className="bg-card overflow-hidden rounded-3xl border border-border">
            <div className="aspect-[4/3] w-full bg-muted animate-pulse" />
            <div className="p-7 space-y-4">
              <div className="h-6 w-20 bg-muted animate-pulse rounded-lg" />
              <div className="h-6 w-full bg-muted animate-pulse rounded-md" />
            </div>
          </div>
        </div>
      </>;
  }
  if (error) {
    return <div className="text-base text-muted-foreground">
        Unable to load articles right now. Please check back soon.
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="text-base text-muted-foreground">
        No articles to display yet.
      </div>;
  }
  return <>
      {}
      <div className="hidden md:grid grid-cols-3 gap-7">
        {posts.map((post, i) => <ArticleCard key={post.id} post={post} index={i} />)}
      </div>

      {}
      <div className="md:hidden">
        <Carousel opts={{
        align: "start",
        loop: false
      }}>
          <CarouselContent className="-ml-4">
            {posts.map((post, i) => <CarouselItem key={post.id} data-index={i} className="pl-4 basis-[85%]">
                <ArticleCard post={post} index={i} />
              </CarouselItem>)}
          </CarouselContent>
        </Carousel>
      </div>
    </>;
}
export default function LatestGuidesTips() {
  const wp_query_params = {
    post_type: "post",
    per_page: 3,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="6129" id="latest-guides-tips" className="bg-background text-foreground py-20 md:py-28 px-5 md:px-14">
      <div className="mx-auto max-w-[1240px]">
        {}
        <div className="mb-14 md:mb-16 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div className="flex flex-col gap-4 max-w-2xl">
            <span className="text-xs font-bold uppercase tracking-[0.15em] text-primary">
              Preparedness Hub
            </span>
            <h2 className="font-serif font-medium leading-[1.08] text-foreground" style={{
            fontSize: "clamp(2rem, 4.2vw, 3.5rem)",
            letterSpacing: "-0.02em"
          }}>
              Stay ready for{" "}
              <em className="italic font-medium" style={{
              color: "var(--primary)"
            }}>
                everything
              </em>
              .
            </h2>
          </div>

          <div className="hidden md:block">
            <Link to="/PreparednessHubPage" className="inline-flex items-center gap-2 text-base font-semibold text-foreground underline underline-offset-[6px] decoration-border hover:decoration-[var(--primary)] hover:text-[var(--primary)] transition-colors duration-200">
              View all guides
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>

        {}
        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 3,
        order: "desc",
        orderby: "date"
      }}>
          <PlaytimeConsumer />
        </PostsProvider>

        {}
        <div className="md:hidden mt-10 flex justify-center">
          <Link to="/PreparednessHubPage">
            <Button className="rounded-lg px-8 py-6 font-semibold bg-primary text-primary-foreground hover:opacity-90">
              View all guides
              <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>
    </section>;
}